/**
 * Relay subscription wrapper around nostr-tools SimplePool.
 *
 * Subscribes to the coordinator's CI events (kinds 39842 / 9841 / 9842) across
 * the configured relays and dispatches each raw event to a per-kind callback.
 *
 * CRITICAL FILTER (NIP-C1): job results (9841) are signed by the COMPUTE
 * PROVIDER, NOT the coordinator. So coordinator-signed kinds (39842 / 9842)
 * are filtered by `authors:[coordinator]`, while 9841 is subscribed with NO
 * author filter and filtered client-side by the coordinator pubkey inside each
 * 9841's `q` tag (`39842:<coordinator>:<run-id>`). See docs/architecture.md.
 *
 * Reconnection is handled by SimplePool's `enableReconnect`; the wrapper also
 * exposes `close()` for teardown on page unload (Gate 2.6b) and `reconnect()`
 * to manually tear down and re-establish the subscription (T6). Connection
 * state is tracked via a `ConnectionTracker` so the UI can show a live-dot and
 * a "connection lost" indicator.
 */
import { SimplePool } from 'nostr-tools';
import type { Filter } from 'nostr-tools';
import { COORDINATOR_PUBKEY, RELAYS } from './config';
import { EVENT_KINDS } from './types';
import type { RawEvent } from './types';

/** Per-kind event callback. */
export type EventHandler = (event: RawEvent) => void;

/** Minimal closer shape returned by SimplePool.subscribeMany. */
interface SubCloser {
  close: (reason?: string) => void;
}

/**
 * Tracks whether at least one configured relay is connected. The dashboard is
 * considered "connected" when ANY relay is reachable (events stream from the
 * first relay that delivers them), and "disconnected" only when every relay
 * is down. Subscribers are notified on every state change.
 */
export class ConnectionTracker {
  private states = new Map<string, boolean>();
  private listeners = new Set<(connected: boolean) => void>();

  constructor(relays: readonly string[]) {
    for (const url of relays) this.states.set(url, false);
  }

  /** True when at least one configured relay is connected. */
  get connected(): boolean {
    for (const connected of this.states.values()) {
      if (connected) return true;
    }
    return false;
  }

  /** Subscribe to connection-state changes. Returns an unsubscribe function. */
  subscribe(listener: (connected: boolean) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Record a relay as connected (ignored if not a configured relay). */
  markConnected(url: string): void {
    if (!this.states.has(url)) return;
    const before = this.connected;
    this.states.set(url, true);
    if (this.connected !== before) this.emit();
  }

  /** Record a relay as disconnected (ignored if not a configured relay). */
  markDisconnected(url: string): void {
    if (!this.states.has(url)) return;
    const before = this.connected;
    this.states.set(url, false);
    if (this.connected !== before) this.emit();
  }

  private emit(): void {
    const connected = this.connected;
    for (const listener of this.listeners) listener(connected);
  }
}

export interface RelaySubscription {
  /** Tear down the subscription and close relay connections. */
  close: () => void;
  /** Tear down and re-establish the subscription (manual reconnect). */
  reconnect: () => void;
  /** The relays currently subscribed. */
  relays: readonly string[];
  /** Subscribe to connection-state changes. Returns an unsubscribe function. */
  onConnectionChange: (listener: (connected: boolean) => void) => () => void;
}

/**
 * Build the relay filters for the coordinator's CI events.
 *
 * Coordinator-signed kinds (39842 / 9842) are filtered by author. Job results
 * (9841) are signed by the compute provider, so they carry NO author filter —
 * the client must verify the coordinator pubkey in each 9841's `q` tag.
 */
export function buildCiFilters(pubkey: string): Filter[] {
  return [
    {
      kinds: [EVENT_KINDS.WORKFLOW_PROGRESS, EVENT_KINDS.WORKFLOW_RESULT],
      authors: [pubkey],
    },
    {
      kinds: [EVENT_KINDS.JOB_RESULT],
    },
  ];
}

/**
 * True when a 9841 job result references the given coordinator in its `q`
 * tag (`39842:<coordinator>:<run-id>`). Used to filter provider-signed job
 * results client-side, since the relay filter cannot restrict by author.
 */
export function isCoordinatorJobResult(event: RawEvent, coordinator: string): boolean {
  for (const tag of event.tags) {
    if (tag[0] === 'q' && tag[1]?.startsWith(`39842:${coordinator}:`)) {
      return true;
    }
  }
  return false;
}

/**
 * Subscribe to the coordinator's CI events. `handlers` maps a kind to the
 * callback invoked for each matching event. Returns a handle with `close()`
 * and `reconnect()`. Connection state is tracked and surfaced via
 * `onConnectionChange` / `connected`.
 */
export function subscribeCiEvents(
  handlers: Partial<Record<number, EventHandler>>,
  opts?: { relays?: string[]; pubkey?: string },
): RelaySubscription {
  const relays = opts?.relays ?? RELAYS;
  const pubkey = opts?.pubkey ?? COORDINATOR_PUBKEY;

  const tracker = new ConnectionTracker(relays);

  let pool: SimplePool | null = null;
  let closers: SubCloser[] = [];
  let statusTimer: ReturnType<typeof setInterval> | null = null;

  const handleEvent = (event: RawEvent): void => {
    // 9841 is signed by the compute provider; only forward it when it
    // references our coordinator in its `q` tag.
    if (event.kind === EVENT_KINDS.JOB_RESULT && !isCoordinatorJobResult(event, pubkey)) {
      return;
    }
    const handler = handlers[event.kind];
    if (handler) handler(event);
  };

  /** Poll the pool's live connection status into the tracker. */
  const pollStatus = (): void => {
    if (!pool) return;
    const status = pool.listConnectionStatus();
    for (const url of relays) {
      if (status.get(url)) tracker.markConnected(url);
      else tracker.markDisconnected(url);
    }
  };

  /** Establish the pool + subscriptions. Safe to call repeatedly (reconnect). */
  const establish = (): void => {
    pool = new SimplePool({ enableReconnect: true });

    // Two filters: coordinator-signed kinds (39842/9842) by author, and 9841
    // with no author filter (provider-signed, checked client-side).
    closers = buildCiFilters(pubkey).map((filter) =>
      pool!.subscribeMany(relays, filter, {
        onevent: (event) => handleEvent(event as RawEvent),
        onclose: () => {
          // SimplePool reconnects automatically; nothing to do here.
        },
      }),
    );

    // Track connection state by polling the pool's live status. SimplePool
    // reconnects automatically, so this reflects real relay connectivity.
    statusTimer = setInterval(pollStatus, 2000);
    pollStatus();
  };

  /** Tear down the pool/subscriptions and stop the status poll. */
  const teardown = (): void => {
    if (statusTimer) {
      clearInterval(statusTimer);
      statusTimer = null;
    }
    for (const closer of closers) closer.close();
    pool?.close(relays);
    pool = null;
    closers = [];
  };

  establish();

  return {
    close: () => {
      teardown();
    },
    reconnect: () => {
      // Tear down the current pool/subscriptions, then re-establish fresh ones.
      teardown();
      establish();
    },
    relays,
    onConnectionChange: (listener) => tracker.subscribe(listener),
  };
}
